import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSupabaseClient } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LoadingState, ErrorState } from "@/components/states";
import { toast } from "@/lib/toast";
import { UserMinus as UserX, ShieldCheck, Plus, Trash as Trash2, MagnifyingGlass as Search, Warning as AlertTriangle } from "@phosphor-icons/react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  updateUserPlan,
  fetchWhitelist,
  addToWhitelist,
  removeFromWhitelist,
  deleteUser,
} from "../api/adminUsers";
import { fetchUserActivity, type UserActivityRow } from "../api/adminMonitoring";
import { UserActivityDrawer } from "./UserActivityDrawer";
import type { UserPlan } from "@/types/supabase";

const INACTIVE_THRESHOLD_DAYS = 7;

function formatRelative(iso: string | null): string {
  if (!iso) return "Nunca";
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `${minutes}m atrás`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h atrás`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d atrás`;
  return new Date(iso).toLocaleDateString("pt-BR");
}

function formatUsd(n: number): string {
  if (n === 0) return "$0";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

// ─── Plan badge ──────────────────────────────────────────────────────────────

const PLAN_CONFIG: Record<UserPlan, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  beta:    { label: "Beta",      variant: "default" },
  pending: { label: "Pendente",  variant: "secondary" },
  blocked: { label: "Bloqueado", variant: "destructive" },
};

function PlanBadge({ plan }: { plan: UserPlan }) {
  const { label, variant } = PLAN_CONFIG[plan] ?? PLAN_CONFIG.pending;
  return <Badge variant={variant}>{label}</Badge>;
}

// ─── User row ────────────────────────────────────────────────────────────────

interface UserRowProps {
  user: UserActivityRow;
  onPlanChange: (userId: string, plan: UserPlan) => void;
  onDelete: (userId: string, name: string) => void;
  onSelect: (userId: string) => void;
  isPending: boolean;
  isDeleting: boolean;
}

function UserRow({ user, onPlanChange, onDelete, onSelect, isPending, isDeleting }: UserRowProps) {
  const label = user.display_name ?? user.email ?? user.user_id;
  const inactive =
    user.days_inactive !== null && user.days_inactive >= INACTIVE_THRESHOLD_DAYS;
  const neverUsed = user.last_use_at === null;
  return (
    <tr
      className={`border-t border-border/30 hover:bg-muted/30 transition-colors cursor-pointer ${
        inactive ? "bg-destructive/[0.04]" : ""
      }`}
      onClick={() => onSelect(user.user_id)}
    >
      <td className="px-3 py-2.5 text-sm text-foreground max-w-[220px] truncate">
        {user.email ?? "—"}
      </td>
      <td className="px-3 py-2.5 text-sm text-muted-foreground">
        {user.display_name ?? "—"}
      </td>
      <td className="px-3 py-2.5">
        <PlanBadge plan={user.plan} />
      </td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
        {formatRelative(user.last_login_at)}
      </td>
      <td className="px-3 py-2.5 text-xs whitespace-nowrap">
        <span className={inactive ? "text-destructive font-medium" : "text-foreground/80"}>
          {formatRelative(user.last_use_at)}
        </span>
      </td>
      <td className="px-3 py-2.5 text-xs whitespace-nowrap">
        {neverUsed ? (
          <span className="text-muted-foreground italic">nunca usou</span>
        ) : inactive ? (
          <span className="inline-flex items-center gap-1 text-destructive font-medium">
            <AlertTriangle className="w-3 h-3" /> {user.days_inactive}d
          </span>
        ) : (
          <span className="text-foreground/70">{user.days_inactive ?? 0}d</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs text-foreground/80 font-mono">
        {user.events_7d}
      </td>
      <td className="px-3 py-2.5 text-xs text-foreground/80 font-mono">
        {formatUsd(Number(user.cost_usd_7d))}
      </td>
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
        <div className="flex gap-1.5">
          {user.plan !== "blocked" && user.role !== "admin" && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 text-xs text-destructive border-destructive/40 hover:bg-destructive/10"
              disabled={isPending || isDeleting}
              onClick={() => onPlanChange(user.user_id, "blocked")}
            >
              <UserX className="w-3 h-3" /> Bloquear
            </Button>
          )}
          {user.role !== "admin" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 w-7 p-0 text-destructive border-destructive/40 hover:bg-destructive/10"
                  disabled={isPending || isDeleting}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir usuário?</AlertDialogTitle>
                  <AlertDialogDescription>
                    <strong>{label}</strong> será removido do sistema. Os materiais (consultas, roteiros, criativos) são preservados. Esta ação não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => onDelete(user.user_id, label)}
                  >
                    Excluir
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Whitelist panel ─────────────────────────────────────────────────────────

function WhitelistPanel({ currentUserId }: { currentUserId: string }) {
  const { client, isReady } = useSupabaseClient();
  const qc = useQueryClient();
  const [newEmail, setNewEmail] = useState("");

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["admin", "whitelist", isReady],
    queryFn: () => fetchWhitelist(client),
    enabled: isReady,
  });

  const add = useMutation({
    mutationFn: (email: string) => addToWhitelist(client, email, currentUserId),
    onSuccess: () => {
      toast.success("Email adicionado à whitelist");
      setNewEmail("");
      qc.invalidateQueries({ queryKey: ["admin", "whitelist"] });
    },
    onError: () => toast.error("Falha ao adicionar email"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => removeFromWhitelist(client, id),
    onSuccess: () => {
      toast.success("Email removido");
      qc.invalidateQueries({ queryKey: ["admin", "whitelist"] });
    },
    onError: () => toast.error("Falha ao remover email"),
  });

  const handleAdd = () => {
    const email = newEmail.trim();
    if (!email || !email.includes("@")) return;
    add.mutate(email);
  };

  return (
    <div className="mt-8">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-primary" /> Whitelist de Acesso
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Exceção manual: emails nesta lista recebem acesso quando entram ou atualizam a sessão.
        </p>
      </div>

      <div className="flex gap-2 mb-4">
        <Input
          placeholder="email@exemplo.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          className="max-w-xs h-8 text-sm"
        />
        <Button
          size="sm"
          className="h-8 gap-1"
          disabled={!newEmail.trim() || add.isPending}
          onClick={handleAdd}
        >
          <Plus className="w-3 h-3" /> Adicionar
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando...</p>
      ) : entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum email na whitelist.</p>
      ) : (
        <div className="border border-border/40 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-t border-border/30 first:border-0 hover:bg-muted/30">
                  <td className="px-3 py-2 text-foreground">{entry.email}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {new Date(entry.created_at).toLocaleDateString("pt-BR")}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-destructive hover:bg-destructive/10"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(entry.id)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main section ────────────────────────────────────────────────────────────

export function UsersSection() {
  const { client, isReady } = useSupabaseClient();
  const { user } = useCurrentUser();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState<UserPlan | "all">("all");

  const [onlyInactive, setOnlyInactive] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ["admin", "user_activity", isReady],
    queryFn: () => fetchUserActivity(client),
    enabled: isReady,
    refetchInterval: 60_000,
  });

  const planMutation = useMutation({
    mutationFn: ({ userId, plan }: { userId: string; plan: UserPlan }) =>
      updateUserPlan(client, userId, plan),
    onSuccess: () => {
      toast.success("Plano atualizado");
      qc.invalidateQueries({ queryKey: ["admin", "user_activity"] });
    },
    onError: () => toast.error("Falha ao atualizar plano"),
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => deleteUser(client, userId),
    onSuccess: (_, userId) => {
      toast.success("Usuário excluído");
      qc.setQueryData(
        ["admin", "user_activity", isReady],
        (prev: UserActivityRow[] | undefined) =>
          prev?.filter((u) => u.user_id !== userId) ?? [],
      );
    },
    onError: () => toast.error("Falha ao excluir usuário"),
  });

  const filtered = users.filter((u) => {
    const matchesPlan = planFilter === "all" || u.plan === planFilter;
    const matchesSearch =
      !search ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.display_name?.toLowerCase().includes(search.toLowerCase());
    const matchesInactive =
      !onlyInactive ||
      u.days_inactive === null ||
      u.days_inactive >= INACTIVE_THRESHOLD_DAYS;
    return matchesPlan && matchesSearch && matchesInactive;
  });

  const inactiveCount = users.filter(
    (u) => u.plan === "beta" && (u.days_inactive === null || u.days_inactive >= INACTIVE_THRESHOLD_DAYS),
  ).length;

  const counts = {
    all: users.length,
    beta: users.filter((u) => u.plan === "beta").length,
    pending: users.filter((u) => u.plan === "pending").length,
    blocked: users.filter((u) => u.plan === "blocked").length,
  };

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground">Usuários</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Contas que já concluíram cadastro. A liberação normal ocorre em Inscrições Beta; aqui você apenas ajusta bloqueios ou exceções.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por email ou nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm w-64"
          />
        </div>
        <div className="flex gap-1.5">
          {(["all", "beta", "pending", "blocked"] as const).map((p) => (
            <Button
              key={p}
              size="sm"
              variant={planFilter === p ? "default" : "outline"}
              className="h-7 text-xs"
              onClick={() => setPlanFilter(p)}
            >
              {p === "all" ? "Todos" : PLAN_CONFIG[p].label} ({counts[p]})
            </Button>
          ))}
        </div>
        <Button
          size="sm"
          variant={onlyInactive ? "default" : "outline"}
          className={`h-7 gap-1 text-xs ${
            onlyInactive ? "" : "text-destructive border-destructive/40 hover:bg-destructive/10"
          }`}
          onClick={() => setOnlyInactive((v) => !v)}
        >
          <AlertTriangle className="w-3 h-3" />
          Inativos ≥ {INACTIVE_THRESHOLD_DAYS}d ({inactiveCount})
        </Button>
      </div>

      {/* Table */}
      <div className="border border-border/40 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Email</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Nome</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Plano</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Último login</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Último uso</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Inativo</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Eventos 7d</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Custo 7d</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-sm text-muted-foreground">
                  Nenhum usuário encontrado.
                </td>
              </tr>
            ) : (
              filtered.map((u) => (
                <UserRow
                  key={u.user_id}
                  user={u}
                  onPlanChange={(userId, plan) => planMutation.mutate({ userId, plan })}
                  onDelete={(userId) => deleteMutation.mutate(userId)}
                  onSelect={(userId) => setSelectedUserId(userId)}
                  isPending={planMutation.isPending}
                  isDeleting={deleteMutation.isPending && deleteMutation.variables === u.user_id}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {user && <WhitelistPanel currentUserId={user.id} />}

      <UserActivityDrawer
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
      />
    </div>
  );
}
